import { useState } from 'react';
import ReactMarkdown from 'react-markdown';

export default function AIAnalysis({ workoutLog, bodyweightLog, bodyweightGoal, language, user, recap, muscleHeat7, muscleHeat30 }) {
  const t = {
    today: language === 'da' ? 'I dag' : "Today's Session",
    week: language === 'da' ? 'Denne uge' : 'This Week',
    month: language === 'da' ? 'Denne måned' : 'This Month',
    analyse: language === 'da' ? 'Analysér træning' : 'Analyse workout',
    analysing: language === 'da' ? 'Analyserer...' : 'Analysing...',
    error: language === 'da' ? 'Fejl ved forbindelse til AI. Sørg for at serveren kører!' : 'Error connecting to AI. Make sure the server is running!',
    noWorkout: language === 'da' ? 'Ingen træning logget i dag endnu!' : 'No workout logged today yet!',
  };
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('today');

  async function analyze() {
    setLoading(true);
    setAnalysis('');

    const today = new Date().toLocaleDateString('en-GB');
    let prompt = '';

    if (mode === 'today') {
      const todayData = workoutLog[today];
      if (!todayData) {
        setAnalysis(t.noWorkout);
        setLoading(false);
        return;
      }
      const bw = Array.isArray(bodyweightLog) ? bodyweightLog.slice(-14) : [];
      prompt = `You are an expert personal trainer analyzing a workout log. Respond in ${language === 'da' ? 'Danish' : 'English'}.

STRICT FORMATTING RULES:
- NEVER use markdown tables
- Use ## for main sections, ### for subsections
- Use bullet points for lists
- Use **bold** for key numbers and important terms
- Keep it concise and punchy
- Max 3-4 sections

Analyze this workout and give specific feedback on performance, intensity (based on RIR if available), and 2-3 concrete suggestions for next time.

Workout data: ${JSON.stringify(todayData.exercises)}

Local session recap (already computed — use these numbers, do not invent others): ${JSON.stringify(recap || {})}
Program day: ${recap?.programDayName || todayData.programDayName || 'none'}

Bodyweight data (recent): ${JSON.stringify(bw)}`;
      if (bodyweightGoal) {
        prompt += `\n\nBodyweight goal: ${JSON.stringify(bodyweightGoal)}`;
      }
    } else if (mode === 'week') {
      const weekData = Object.entries(workoutLog)
        .slice(-7)
        .map(([date, w]) => ({ date, exercises: w.exercises }));
      const bw = Array.isArray(bodyweightLog) ? bodyweightLog.slice(-14) : [];
      prompt = `You are an expert personal trainer analyzing a week of workouts. Respond in ${language === 'da' ? 'Danish' : 'English'}.

STRICT FORMATTING RULES:
- NEVER use markdown tables
- Use ## for main sections, ### for subsections
- Use bullet points for lists
- Use **bold** for key numbers and important terms
- Max 4-5 sections
- Be specific and actionable

Analyze this week of training. Cover: muscle group balance, volume, progression, and give 3 key recommendations.

Week data: ${JSON.stringify(weekData)}

Muscle set distribution last 7 days (app model): ${JSON.stringify(muscleHeat7 || {})}

Bodyweight data (recent): ${JSON.stringify(bw)}`;
      if (bodyweightGoal) {
        prompt += `\n\nBodyweight goal: ${JSON.stringify(bodyweightGoal)}`;
      }
    } else if (mode === 'month') {
      const monthData = Object.entries(workoutLog)
        .slice(-30)
        .map(([date, w]) => ({ date, exercises: w.exercises }));
      const bw = Array.isArray(bodyweightLog) ? bodyweightLog.slice(-60) : [];
      prompt = `You are an expert personal trainer analyzing a month of training. Respond in ${language === 'da' ? 'Danish' : 'English'}.

STRICT FORMATTING RULES:
- NEVER use markdown tables
- Use ## for main sections, ### for subsections
- Use bullet points for lists
- Use **bold** for key numbers and important terms
- Max 5 sections
- Be specific and actionable

Analyze this month of training. Cover: consistency, progressive overload trends, muscle group balance, and give 3-4 key recommendations for next month.

Month data: ${JSON.stringify(monthData)}

Muscle set distribution last 30 days (app model): ${JSON.stringify(muscleHeat30 || {})}

Bodyweight data (recent): ${JSON.stringify(bw)}`;
      if (bodyweightGoal) {
        prompt += `\n\nBodyweight goal: ${JSON.stringify(bodyweightGoal)}`;
      }
    }

    try {
      const idToken = user ? await user.getIdToken() : null;
      if (!idToken) throw new Error('Missing auth token');

     const res = await fetch(process.env.REACT_APP_API_URL || '/api/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ prompt })
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      setAnalysis(data.result);
    } catch (err) {
      setAnalysis(t.error);
    }
    setLoading(false);
  }

const modes = [
    { key: 'today', label: t.today },
    { key: 'week', label: t.week },
    { key: 'month', label: t.month },
  ];

  return (
    <div>
      {/* Mode selector */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {modes.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className="btn"
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              background: mode === m.key ? 'var(--accent)' : 'var(--surface2)',
              color: mode === m.key ? 'white' : 'var(--text)',
              border: '1px solid var(--border)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
            {m.label}
          </button>
        ))}
      </div>

      <button
        className="btn btn-primary"
        onClick={analyze}
        disabled={loading}
        style={{ width: '100%', padding: '12px', fontSize: '15px', marginBottom: '16px', opacity: loading ? 0.7 : 1 }}>
        {t.analyse}
      </button>

      {loading && (
        <div className="ai-loading">
          <div className="ai-loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="ai-loading-text">{t.analysing}</div>
        </div>
      )}
      {!loading && analysis && (
        <div className="ai-output" style={{ background: 'var(--surface2)', border: '1px solid var(--accent)', borderRadius: '10px', padding: '16px', fontSize: '14px', color: 'var(--text)' }}>
          <ReactMarkdown>{analysis}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}